import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { RootState } from '../../../../core/data/redux/store';
import { fetchAllSchools } from '../../../../core/data/services/schoolService';
import { setSchoolList, setSelectedSchool } from '../../../../core/data/redux/schoolSlice';
import { calculateDistanceInMeters } from '../../../../utils/geoUtils';
import SuccessModal from '../../../../core/common/successModal';
import FailureModal from '../../../../core/common/ErrorModal';

const ALLOWED_RADIUS = 150; // Allow attendance if within 150m of school (100m + 50m buffer)

const TeacherLocationChecker: React.FC = () => {
  const dispatch = useDispatch();
  const {  selectedSchool } = useSelector((state: RootState) => state.school);
  const user = useSelector((state: RootState) => state.user.Users);
  const currentUser = user.length > 0 ? user[0] : null;
  const schoolName = currentUser?.relatedEntity?.schools?.name || 'School Name Not Available';
  
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [distance, setDistance] = useState<number | null>(null);
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showFailure, setShowFailure] = useState(false);
  const [failureMessage, setFailureMessage] = useState('');

  // Fetch schools and set the user's school
  useEffect(() => {
    const loadSchools = async () => {
      try {
        const schools = await fetchAllSchools();
        dispatch(setSchoolList(schools));
        
        // Find the school that matches the user's school name
        const userSchool = schools.find((school: any) => school.name === schoolName);
        if (userSchool) {
          dispatch(setSelectedSchool(userSchool));
        } else {
          setFailureMessage('School not found in the system.');
          setShowFailure(true);
        }
      } catch (error) {
        console.error('Error fetching schools:', error);
        setFailureMessage('Failed to fetch school data.');
        setShowFailure(true);
      }
    };
    if (schoolName !== 'School Name Not Available') {
      loadSchools();
    }
  }, [dispatch, schoolName]);

  // Check distance when user location and selected school are available
  useEffect(() => {
    if (selectedSchool && userLocation) {
      const dist = calculateDistanceInMeters(
        userLocation.lat,
        userLocation.lng,
        selectedSchool.latitude,
        selectedSchool.longitude
      );
      setDistance(dist);

      const isInside = dist <= ALLOWED_RADIUS;
      const message = isInside
        ? 'You are within the allowed radius of the school. Attendance marking is allowed.'
        : 'You are outside the allowed radius. You are not allowed to mark attendance.';

      setStatus(message);

      if (isInside) {
        setSuccessMessage(message);
        setShowSuccess(true);
      } else {
        setFailureMessage(message);
        setShowFailure(true);
      }
    }
  }, [selectedSchool, userLocation]);

  const handleLocationCheck = () => {
    if (!selectedSchool) {
      setFailureMessage('School data is not available. Please try again later.');
      setShowFailure(true);
      return;
    }

    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setIsLoading(false);
      },
      (err) => {
        console.error('Geolocation error:', err);
        setFailureMessage('Failed to retrieve your current location.');
        setShowFailure(true);
        setIsLoading(false);
      }
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content content-two">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Teacher Location Checker</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Location Checker
                </li>
              </ol>
            </nav>
          </div>
        </div>

        {/* Card */}
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
            <h4 className="mb-3">Check Your Current Location</h4>
          </div>

          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Your School</label>
                <p className="form-control-static">{schoolName}</p>
              </div>

              <div className="col-md-6 d-flex align-items-end">
                <button className="btn btn-primary" onClick={handleLocationCheck} disabled={isLoading}>
                  <i className="ti ti-map-pin-check me-2" />
                  {isLoading ? 'Checking...' : 'Check My Location'}
                  {isLoading && <span className="spinner-border spinner-border-sm ms-2" />}
                </button>
              </div>
            </div>

            {/* Coordinates & Distance */}
            {userLocation && selectedSchool && (
              <div className="mt-4">
                <div className="mb-2">
                  <strong>📍 Your Coordinates:</strong><br />
                  Lat: {userLocation.lat.toFixed(6)} | Lng: {userLocation.lng.toFixed(6)}
                </div>
                <div className="mb-2">
                  <strong>🏫 School Coordinates:</strong><br />
                  Lat: {selectedSchool.latitude.toFixed(6)} | Lng: {selectedSchool.longitude.toFixed(6)}
                </div>
              </div>
            )}

            {distance !== null && (
              <div className="mt-3">
                <p className="mb-1">
                  📏 <strong>Distance:</strong>{' '}
                  {distance < 1000
                    ? `${Math.round(distance)} meters`
                    : `${(distance / 1000).toFixed(2)} km`}
                </p>
                <p className={status.includes('within') ? 'text-success' : 'text-danger'}>
                  {status}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <SuccessModal
        show={showSuccess}
        message={successMessage}
        onClose={() => setShowSuccess(false)}
      />
      <FailureModal
        show={showFailure}
        message={failureMessage}
        onClose={() => setShowFailure(false)}
      />
    </div>
  );
};

export default TeacherLocationChecker;

